import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import RemoveIcon from 'assets/icons/remove-icon'
import ConfirmIcon from 'assets/icons/confirm-icon'
import RejectIcon from 'assets/icons/reject-icon'
import AssessIcon from 'assets/icons/assess-icon'
import ReassessIcon from 'assets/icons/reassess-icon'
import PulsingNotification from 'features/PulsingNotification'
import CancelIcon from 'assets/icons/cancel-icon16px'
import { SiteContext } from 'providers/site'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import { SITE_STATUS_DRAFT_ID, SITE_STATUS_NOMINATED_ID } from 'constants/siteStatuses'
import { ProfileContext } from 'providers/profile'
import { SECRETARIAT_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID, INDIVIDUAL_ROLE_ID } from 'constants/roles'
import ActionsMenu from 'features/ActionMenu'
import { confirmCriterionE, cancelCriterionEReassessmentRequest } from 'api/criterionE'
import Tooltip from 'components/Tooltip'
import Button from 'components/Button'
import Modal from 'components/Modal'
import FlowRequestModal from 'components/FlowRequestModal'
import CalendarIcon from 'assets/icons/calendar-icon'
import { reassessmentTriggerReasons } from 'constants/reassessmentTriggerReasons'
import DropdownIndicatorIconOpen from 'assets/icons/dropdown-indicator-opened24px'
import DropdownIndicatorIconClosed from 'assets/icons/dropdown-indicator-closed24px'
import { showErrorToast } from 'utils/toast'
import { getCriterionEId, hasQualifyingReason } from 'utils/criterionEHelpers'
import { getReassessmentActionId } from 'utils/ecosystemHeaderHelpers'
import {
  EcosystemActionMenuWrapper,
  EcosystemCalendarDateText, EcosystemCalendarDivider, EcosystemCalendarIconWrapper,
  EcosystemConfirmDeclineIcon,
  EcosystemHeaderIcon,
  EcosystemHeaderIconWrapper,
  EcosystemHeaderNavbarHalfSide,
  EcosystemHeaderNavbarIconWrapper,
  EcosystemHeaderNavbarWrapper,
  EcosystemHeaderTitle, EcosystemLastReassessmentCalendarWrapper, EcosystemToggleWrapper,
  EcoSystemValidationIcon, EcosystemValidationIconWrapper, EcosystemsCancelIconWrapper
} from '../ecosystemData/style'

const formattedRejectIconTooltipHint = (item) => {
  return (item.trigger_reason_id || []).map((element) => {
    return reassessmentTriggerReasons.find((elementId) => {
      return elementId.id === Number(element)
    })?.name
  }).filter(Boolean)
}


const tooltipXOffset = 0
const tooltipYOffset = -16
const criterionEItemPropType = PropTypes.shape({
  site_irreplaceability_history_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  site_irreplaceability_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  updated_at: PropTypes.string,
  trigger_reason_id: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  last_reassessment_date: PropTypes.string,
  is_approved_by_secretariat: PropTypes.bool
})

const dateOptions = {
  year: 'numeric',
  month: 'short',
  weekday: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hour12: true
}

const ReassessmentDate = ({ hasDateToShow, dateToShow }) => {
  if (!hasDateToShow) {
    return null
  }

  return (
    <Tooltip
      style={{ zIndex: 1000 }}
      placement='top'
      trigger={['hover']}
      align={{ offset: [tooltipXOffset, tooltipYOffset] }}
      overlay={{ label: 'lastTimeReassessed' }}
    >
      <EcosystemLastReassessmentCalendarWrapper>
        <EcosystemCalendarIconWrapper>
          <CalendarIcon />
        </EcosystemCalendarIconWrapper>
        <EcosystemCalendarDateText>
          {dateToShow.toLocaleDateString('en-US', dateOptions)}
        </EcosystemCalendarDateText>
        <EcosystemCalendarDivider />
      </EcosystemLastReassessmentCalendarWrapper>
    </Tooltip>
  )
}

const RemoveCriterionEButton = ({ item, openRemoveCriterionEModal, isDraftAndEnabled, isReassessmentMode }) => {
  if (!isDraftAndEnabled) {
    return null
  }

  if (isReassessmentMode && item.last_reassessment_date) {
    return null
  }

  return (
    <EcosystemHeaderNavbarIconWrapper
      delete={isReassessmentMode}
      data-testid='remove-button'
      onClick={(e) => {
        e.stopPropagation()
        openRemoveCriterionEModal(item)
      }}
    >
      <RemoveIcon />
    </EcosystemHeaderNavbarIconWrapper>
  )
}

const HeaderActions = ({
  site,
  profile,
  item,
  isOpen,
  oldTriggers,
  toggleAccordion,
  openRemoveCriterionEModal,
  fieldsToCompleteNumber,
  actionMenuItems,
  disabled,
  setCriterionEToCancel,
  setIsCancelOpen,
  hasDateToShow,
  dateToShow,
  reassessmentActionId,
  qulifyes
}) => {
  const isDraft = site.SiteStatusId === SITE_STATUS_DRAFT_ID
  const isNominatedSecretariat = site.SiteStatusId === SITE_STATUS_NOMINATED_ID && (profile.RoleId === SECRETARIAT_ROLE_ID || profile.RoleId === BIRDLIFE_SECRETARIAT_ROLE_ID)
  const isDraftAndEnabled = isDraft && !disabled
  const hasIncompleteFields = fieldsToCompleteNumber.length > 0

  const renderValidationIndicator = () => {
    if (!hasIncompleteFields) {
      return null
    }

    return (
      <EcosystemValidationIconWrapper>
        <EcoSystemValidationIcon>
          <PulsingNotification number={fieldsToCompleteNumber.length} />
        </EcoSystemValidationIcon>
      </EcosystemValidationIconWrapper>
    )
  }

  const renderQualifiedIndicator = () => (
    <EcosystemConfirmDeclineIcon type={qulifyes ? 'confirmed' : 'rejected'}>
      {qulifyes ? (
        <Tooltip
          style={{ zIndex: 1000 }}
          placement='top'
          trigger={['hover']}
          align={{ offset: [tooltipXOffset, tooltipYOffset] }}
          overlay={{ label: 'qualifies' }}
        >
          <div>
            <ConfirmIcon />
          </div>
        </Tooltip>
      ) : (
        <Tooltip
          style={{ zIndex: 1000 }}
          placement='top'
          trigger={['hover']}
          align={{ offset: [tooltipXOffset, tooltipYOffset] }}
          overlay={{ label: 'nonQualifying', infoTooltip: formattedRejectIconTooltipHint(item).join(', ') }}
        >
          <div>
            <RejectIcon />
          </div>
        </Tooltip>
      )}
    </EcosystemConfirmDeclineIcon>
  )

  const renderReassessmentActions = () => (
    <>
      <ReassessmentDate hasDateToShow={hasDateToShow} dateToShow={dateToShow} />
      {renderQualifiedIndicator()}
      {isDraft ? (
        <>
          <Button
            isDisabled={isOpen}
            type={isOpen ? 'defaultDisabled' : ' '}
            height='32px'
            marginTop='0'
            onClick={toggleAccordion}
          >
            <ReassessIcon />
            <FormattedMessage id={reassessmentActionId} />
          </Button>
          {oldTriggers && (
            <EcosystemsCancelIconWrapper onClick={(e) => {
              e.stopPropagation()
              setCriterionEToCancel(item)
              setIsCancelOpen(true)
            }}>
              <CancelIcon />
            </EcosystemsCancelIconWrapper>
          )}
        </>
      ) : (
        <EcosystemToggleWrapper isActive={isOpen} onClick={toggleAccordion}>
          <FormattedMessage id='expand' />
          {isOpen ? <DropdownIndicatorIconOpen /> : <DropdownIndicatorIconClosed />}
        </EcosystemToggleWrapper>
      )}
      <RemoveCriterionEButton
        item={item}
        openRemoveCriterionEModal={openRemoveCriterionEModal}
        isDraftAndEnabled={isDraftAndEnabled}
        isReassessmentMode={true}
      />
    </>
  )

  const renderStandardActions = () => (
    <>
      {isDraft ? (
        <Button
          height='32px'
          onClick={toggleAccordion}
          marginTop='0'
          isDisabled={isOpen}
          type={isOpen ? 'defaultDisabled' : ''}
        >
          <AssessIcon />
          <FormattedMessage id={reassessmentActionId} />
        </Button>
      ) : (
        <>
          <ReassessmentDate hasDateToShow={hasDateToShow} dateToShow={dateToShow} />
          {renderQualifiedIndicator()}
          <EcosystemToggleWrapper isActive={isOpen} onClick={toggleAccordion}>
            <FormattedMessage id='expand' />
            {isOpen ? <DropdownIndicatorIconOpen /> : <DropdownIndicatorIconClosed />}
          </EcosystemToggleWrapper>
        </>
      )}
      <RemoveCriterionEButton
        item={item}
        openRemoveCriterionEModal={openRemoveCriterionEModal}
        isDraftAndEnabled={isDraftAndEnabled}
        isReassessmentMode={false}
      />
      {isNominatedSecretariat && (
        <EcosystemActionMenuWrapper>
          <ActionsMenu table={false} dropdownItems={actionMenuItems} margin='0' />
        </EcosystemActionMenuWrapper>
      )}
    </>
  )

  return (
    <>
      {site.reassessmentFlag ? renderReassessmentActions() : renderStandardActions()}
      {renderValidationIndicator()}
    </>
  )
}
const CriterionEAccordionHeader = ({ isOpen, item, openRemoveCriterionEModal, getCriterionEs, toggleAccordion, lastItem, newTriggers = false, oldTriggers = false }) => {
  const { site, siteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const { profile } = useContext(ProfileContext)
  const isIndividualUser = profile.Role.id === INDIVIDUAL_ROLE_ID
  const dateToShow = item.updated_at ? new Date(item.updated_at) : new Date()
  const hasDateToShow = Boolean(item.updated_at)
  const [isCancelOpen, setIsCancelOpen] = useState(false)
  const [criterionEToCancel, setCriterionEToCancel] = useState(null)
  const criterionId = getCriterionEId(item)
  const reassessmentActionId = getReassessmentActionId(oldTriggers, newTriggers)

  const handleCriterionEAction = async (isApproved) => {
    await confirmCriterionE(criterionId, isApproved)
    getCriterionEs()
  }

  const actionMenuItems = item.is_approved_by_secretariat ? [{
    icon: <RejectIcon />,
    title: 'rejectCriterionE',
    onClick: async () => {
      await handleCriterionEAction({ isApproved: false })
    },
    isDestructive: true
  }] : [{
    icon: <ConfirmIcon />,
    title: 'confirmCriterionE',
    onClick: async () => {
      await handleCriterionEAction({ isApproved: true })
    }
  }]

  const validationCriterionE = siteProposalFieldValidation.criterionE[criterionId]
    ? siteProposalFieldValidation.criterionE[criterionId]
    : defaultSiteProposalValidationResults.criterionE

  const fieldsToCompleteNumber = Object.values(validationCriterionE).filter((element) => {
    return element === false
  })

  const qulifyes = hasQualifyingReason(item.trigger_reason_id)

  const cancelCriterionEsChanges = async (criterionE) => {
    if (!isIndividualUser) {
      showErrorToast('This action is not allowed.')
      return
    }
    const data = await cancelCriterionEReassessmentRequest({ siteId: site.historyId, criterionEId: criterionE.site_irreplaceability_history_id, isCancelled: true })
    if (data.error) {
      return showErrorToast(data.error)
    }
    setIsCancelOpen(false)
    setCriterionEToCancel(null)
    toggleAccordion()
    await getCriterionEs()
  }

  const disabled = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID) || !qulifyes

  return (
    <>
      <EcosystemHeaderNavbarWrapper
        isOpen={isOpen}
        bottomBorder={isOpen || lastItem}
        data-testid='criterionE'
      >
        <EcosystemHeaderNavbarHalfSide onClick={toggleAccordion}>
          <EcosystemHeaderIconWrapper>
            <EcosystemHeaderIcon isRejected={!qulifyes} />
          </EcosystemHeaderIconWrapper>
          <EcosystemHeaderTitle
            isRejected={!qulifyes}
            data-testid='criterionE-name'
            isReassess={item.last_reassessment_date}
          >
            {item?.year}
          </EcosystemHeaderTitle>
        </EcosystemHeaderNavbarHalfSide>
        <EcosystemHeaderNavbarHalfSide>
          <HeaderActions
            site={site}
            profile={profile}
            item={item}
            isOpen={isOpen}
            oldTriggers={oldTriggers}
            toggleAccordion={toggleAccordion}
            openRemoveCriterionEModal={openRemoveCriterionEModal}
            fieldsToCompleteNumber={fieldsToCompleteNumber}
            actionMenuItems={actionMenuItems}
            disabled={disabled}
            setCriterionEToCancel={setCriterionEToCancel}
            setIsCancelOpen={setIsCancelOpen}
            hasDateToShow={hasDateToShow}
            dateToShow={dateToShow}
            reassessmentActionId={reassessmentActionId}
            qulifyes={qulifyes}
          />
        </EcosystemHeaderNavbarHalfSide>
      </EcosystemHeaderNavbarWrapper>
      <Modal
        width='416px'
        isOpen={isCancelOpen}
        toggleModal={setIsCancelOpen}
        modalTitleId='cancelCriterionEReassessment'
      >
        <FlowRequestModal
          cancelHandler={() => {
            setIsCancelOpen(false)
            setCriterionEToCancel(null)
          }}
          confirmHandler={() => {
            cancelCriterionEsChanges(criterionEToCancel)
          }}
          confirmBtnText='yesCancel'
          rejectBtnText='continueEditing'
          text={<FormattedMessage id='returnDiscardCriterionEChangesText' values={{ year: <b>{criterionEToCancel?.year}</b> }} />}
          item={criterionEToCancel}
          approve={true}
        />
      </Modal>
    </>
  )
}

ReassessmentDate.propTypes = {
  hasDateToShow: PropTypes.bool.isRequired,
  dateToShow: PropTypes.instanceOf(Date).isRequired
}

RemoveCriterionEButton.propTypes = {
  item: criterionEItemPropType.isRequired,
  openRemoveCriterionEModal: PropTypes.func.isRequired,
  isDraftAndEnabled: PropTypes.bool.isRequired,
  isReassessmentMode: PropTypes.bool.isRequired
}

HeaderActions.propTypes = {
  site: PropTypes.shape({
    SiteStatusId: PropTypes.number.isRequired,
    reassessmentFlag: PropTypes.bool
  }).isRequired,
  profile: PropTypes.shape({
    RoleId: PropTypes.number
  }).isRequired,
  item: criterionEItemPropType.isRequired,
  isOpen: PropTypes.bool,
  oldTriggers: PropTypes.bool,
  toggleAccordion: PropTypes.func.isRequired,
  openRemoveCriterionEModal: PropTypes.func.isRequired,
  fieldsToCompleteNumber: PropTypes.arrayOf(PropTypes.bool).isRequired,
  actionMenuItems: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.node,
    title: PropTypes.string,
    onClick: PropTypes.func,
    isDestructive: PropTypes.bool
  })).isRequired,
  disabled: PropTypes.bool.isRequired,
  setCriterionEToCancel: PropTypes.func.isRequired,
  setIsCancelOpen: PropTypes.func.isRequired,
  hasDateToShow: PropTypes.bool.isRequired,
  dateToShow: PropTypes.instanceOf(Date).isRequired,
  reassessmentActionId: PropTypes.string.isRequired,
  qulifyes: PropTypes.bool
}

export default CriterionEAccordionHeader

CriterionEAccordionHeader.propTypes = {
  isOpen: PropTypes.bool,
  item: criterionEItemPropType.isRequired,
  openRemoveCriterionEModal: PropTypes.func.isRequired,
  getCriterionEs: PropTypes.func.isRequired,
  toggleAccordion: PropTypes.func.isRequired,
  lastItem: PropTypes.bool,
  newTriggers: PropTypes.bool,
  oldTriggers: PropTypes.bool
}